<script lang="ts">
	import { _ } from "svelte-i18n";
	import { FormGroup, Label } from "@sveltestrap/sveltestrap";
	import type { DropdownOption, V2Persona } from "../../../common/types";
	import PersonaPickerModal from "./PersonaPickerModal.svelte";
	import { CUSTOM_PERSONA_ID, type DraftCustomPersona } from "./customPersonaTypes.ts";

	let {
		personas,
		value = $bindable(""),
		customPersona = null,
		availableVoices = [],
		onSelectCustom,
		onDiscardCustom,
	}: {
		personas: V2Persona[];
		value?: string;
		customPersona?: DraftCustomPersona | null;
		availableVoices?: DropdownOption[];
		onSelectCustom?: (draft: DraftCustomPersona) => void;
		onDiscardCustom?: () => void;
	} = $props();

	let modalOpen = $state(false);

	const selectedPreset = $derived(
		value === CUSTOM_PERSONA_ID ? undefined : personas.find((p) => p.id === value),
	);
	const showingCustom = $derived(value === CUSTOM_PERSONA_ID && customPersona);

	function openModal() {
		modalOpen = true;
	}

	function onSelect(id: string) {
		value = id;
		modalOpen = false;
	}

	function handleSelectCustom(draft: DraftCustomPersona) {
		onSelectCustom?.(draft);
		value = CUSTOM_PERSONA_ID;
		modalOpen = false;
	}

	function handleDiscardCustom() {
		onDiscardCustom?.();
	}
</script>

<FormGroup>
	<Label>{$_("landingpage.newPresentation.persona")}</Label>
	{#if showingCustom && customPersona}
		<button type="button" class="persona-trigger" onclick={openModal}>
			{#if customPersona.imageBase64}
				<img src={customPersona.imageBase64} alt={customPersona.label} />
			{:else}
				<div class="placeholder" aria-hidden="true">👤</div>
			{/if}
			<div class="persona-trigger-text">
				<div class="persona-trigger-name">{customPersona.name}</div>
				<div class="persona-trigger-label">{customPersona.label}</div>
			</div>
			<span class="persona-trigger-change">
				{$_("landingpage.newPresentation.personaChange")}
			</span>
		</button>
	{:else if selectedPreset}
		<button type="button" class="persona-trigger" onclick={openModal}>
			<img src={selectedPreset.imageUrl} alt={selectedPreset.label} />
			<div class="persona-trigger-text">
				<div class="persona-trigger-name">{selectedPreset.name}</div>
				<div class="persona-trigger-label">{selectedPreset.label}</div>
			</div>
			<span class="persona-trigger-change">
				{$_("landingpage.newPresentation.personaChange")}
			</span>
		</button>
	{:else}
		<!--
			Defensive fallback for the (rare) state where `value` is the custom
			sentinel but `customPersona` is null, or where no value is set yet.
			Without this branch the user can't open the modal to recover.
		-->
		<button type="button" class="persona-trigger" onclick={openModal}>
			<div class="placeholder" aria-hidden="true">👤</div>
			<div class="persona-trigger-text">
				<div class="persona-trigger-name">
					{$_("landingpage.newPresentation.personaModalTitle")}
				</div>
			</div>
			<span class="persona-trigger-change">
				{$_("landingpage.newPresentation.personaChange")}
			</span>
		</button>
	{/if}
</FormGroup>

<PersonaPickerModal
	bind:isOpen={modalOpen}
	{personas}
	selectedId={value}
	{customPersona}
	{availableVoices}
	{onSelect}
	onSelectCustom={handleSelectCustom}
	onDiscardCustom={handleDiscardCustom}
/>

<style>
	.persona-trigger {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		width: 100%;
		padding: 0.75rem;
		border: 1px solid var(--bs-border-color, #dee2e6);
		border-radius: 0.5rem;
		background: var(--bs-body-bg, #fff);
		text-align: left;
		cursor: pointer;
		transition:
			border-color 120ms ease,
			box-shadow 120ms ease;
	}
	.persona-trigger:hover,
	.persona-trigger:focus-visible {
		border-color: var(--bs-primary, #0d6efd);
		box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.15);
		outline: none;
	}
	.persona-trigger img,
	.persona-trigger .placeholder {
		width: 64px;
		height: 64px;
		border-radius: 50%;
		object-fit: cover;
		background: #eee;
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 2rem;
	}
	.persona-trigger-text {
		flex-grow: 1;
		min-width: 0;
	}
	.persona-trigger-name {
		font-weight: 600;
	}
	.persona-trigger-label {
		color: var(--bs-secondary, #6c757d);
		font-size: 0.875rem;
	}
	.persona-trigger-change {
		color: var(--bs-primary, #0d6efd);
		font-size: 0.875rem;
		text-decoration: underline;
		flex-shrink: 0;
	}
</style>
